import { useCallback, useState } from 'react';

import { CustomizeTheme } from '../modals/CustomizeTheme';
import ExportDates from '../modals/ExportDates';
import ImportDates from '../modals/ImportDates';
import ImportHolidays from '../modals/ImportHolidays';
import { PageTypeSelector } from '../modals/PageTypeSelector';
import SelectDates from '../modals/SelectDates';
import { ThemeSelector } from '../modals/ThemeSelector';
import { getPageLayout, headerVariables, pageLayouts } from '../pageTypes';
import FormGroup from './FormGroup';
import PageTitle from './PageTitle';
import TimeDuration from './TimeDuration.js';
import WeekSelector from './WeekSelector.js';

import YearsEditor from './YearsEditor';
import Button from '../components/Button.js';
import SliderWithValue from './SliderWithValue.js';
import { IntegerInput } from '../components/IntegerInput.js';
import TitleOptions from './TitleOptions.js';

const Customize = (props) => {
	const { themes, configuration, update } = props;

	/* ---------- colors ---------- */

	const colors = [
		'#ff0000', '#ff4d4d', '#ff9999',
		'#00ff00', '#4dff4d', '#99ff99',
		'#0000ff', '#4d4dff', '#9999ff',
		'#ffbe0b', '#ffd24d', '#ffe799',
		'#fb5607', '#fc7f4d', '#fdab99',
		'#ff006e', '#ff4d9a', '#ff99c2',
		'#8338ec', '#a06cf2', '#c3a3f7',
		'#3a86ff', '#6ba4ff', '#a3c6ff',
		'#000000', '#4d4d4d', '#999999',
		'#ffffff', '#f5f5f5', '#cccccc', '#808080', '#404040',
		'#00bcd4', '#4caf50', '#ff9800', '#795548', '#607d8b'
	];

	const colorOptions = [
		{ id: 'background', label: 'Background', defaultColor: '#ff0000', value: '#ff0000' },
		{ id: 'border', label: 'Border', defaultColor: null, value: null },
		{ id: 'text', label: 'Text', defaultColor: '#000000', value: '#000000' }
	];

	/* ---------- theme ---------- */

	const themeRef = configuration.themeNameOrTheme;

	const activeTheme = (() => {
		// named theme
		if (typeof themeRef === 'string') {
			return themes[themeRef] ?? themes.default;
		}

		// custom theme object
		if (typeof themeRef === 'object' && themeRef !== null) {
			return themeRef;
		}

		// final fallback
		return themes.default;
	})();

	const activeThemeName =
		activeTheme.displayName
		?? (typeof themeRef === 'string' ? themeRef : 'Custom Theme');

	const selectTheme = (name) => {
		update(updateProperty('themeNameOrTheme', name));
	};

	const applyCustomTheme = (themeObject) => {
		update(updateProperty('themeNameOrTheme', themeObject));
	};

	/* ---------- years editor state ---------- */

	const [years, setYears] = useState(() => {
		if (configuration.years?.length) {
			return configuration.years.map(y => ({
				yearInput: y.year,
				year: y.year,
				months: Array.from({ length: 12 }, (_, i) =>
					y.months.includes(i + 1)
				),
				dirty: false
			}));
		}

		const current = new Date().getFullYear();
		return [{
			yearInput: current,
			year: current,
			months: Array(12).fill(true),
			dirty: false
		}];
	});

	/* ---------- modal state ---------- */

	const [showPageTypeSelectorModal, setPageTypeSelectorModal] = useState(false);
	const [showThemeSelectorModal, setShowThemeSelectorModal] = useState(false);
	const [showCustomizeThemeModal, setShowCustomizeThemeModal] = useState(false);
	const [showImportHolidaysDatesModal, setShowImportHolidaysDatesModal] = useState(false);
	const [showSelectDatesModal, setShowSelectDatesModal] = useState(false);
	const [showImportDatesModal, setShowImportDatesModal] = useState(false);
	const [showExportDatesModal, setShowExportDatesModal] = useState(false);

	/* ---------- helpers ---------- */

	const updateSelectedDates = (originalState) => {
		update(updateProperty('selectedDates', originalState));
	};

	const updateCalendarSelection = (paperSize, orientation, layout, theme) => {
		const pageLayout = getPageLayout(layout);
		selectTheme(theme);

		update(updateProperty(
			'paperSize', paperSize,
			'orientation', orientation,
			'layout', layout,
			'pageTitleTemplate',
			pageLayout.pageTitleTemplateConfig.defaultTemplate,
			'themeNameOrTheme', theme
		));
	};

	const updateProperty = (
		key, value,
		key2, value2,
		key3, value3,
		key4, value4,
		key5, value5
	) => {
		const copy = { ...configuration };

		copy[key] = value;
		if (key2 && value2) copy[key2] = value2;
		if (key3 && value3) copy[key3] = value3;
		if (key4 && value4) copy[key4] = value4;
		if (key5 && value5) copy[key5] = value5;

		return copy;
	};

	const updateLayoutConfig = (
		key, value,
		key2, value2,
		key3, value3,
		key4, value4,
		key5, value5
	) => {
		const layoutConfig = { ...configuration?.layoutConfig[configuration.layout] }

		layoutConfig[key] = value;
		if (key2 && value2) layoutConfig[key2] = value2;
		if (key3 && value3) layoutConfig[key3] = value3;
		if (key4 && value4) layoutConfig[key4] = value4;
		if (key5 && value5) layoutConfig[key5] = value5;

		const copy = { ...configuration };
		copy.layoutConfig[configuration.layout] = layoutConfig;

		return copy;
	}

	const layoutConfig = () => {
		return configuration.layoutConfig[configuration.layout];
	}

	const labelFor = (k, label) => (
		<label
			htmlFor={k}
			className="mb-2 uppercase font-bold text-lg text-grey-darkest"
		>
			{label}
		</label>
	);

	const optionSelectedDates = () => configuration.selectedDates ?? [];

	const optionVisible = (option) => {
		const config = getPageLayout(configuration.layout);
		return !config.visibleCustomisationOptions
			|| config.visibleCustomisationOptions.includes(option);
	};

	const orientationValue = () => {
		const config = getPageLayout(configuration.layout);
		const supported = Object.keys(config.factories);

		if (supported.includes(configuration.orientation)) {
			return configuration.orientation;
		}

		configuration.orientation = supported[0];
		return configuration.orientation;
	};

	const canChangeOrientations = () => {
		const config = getPageLayout(configuration.layout);
		return Object.keys(config.factories).length > 1;
	};

	const updateDocumentObject = useCallback(
		(key, value) => update(updateProperty('document', { ...configuration.document, [key]: value })),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[configuration.document, /* update, updateProperty ommited*/]
	);

	const updateDocumentMarginsInMmObject = useCallback(
		(key, value) => update(updateProperty('document', { ...configuration.document, marginsInMm: { ...configuration.document?.marginsInMm ?? {}, [key]: value }})),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[configuration.document, /* update, updateProperty ommited*/]
	);

	const textVariables = getPageLayout(configuration.layout).pageTitleTemplateConfig.variables;
	const descShortHeader = "Header content of the PDF";
	const descLongHeader = null;
	const descShortDocumentTitle = "Title of the PDF";
	const descLongDocumentTitle = "Only added once, at the begining to the PDF.";
	const descShorPageTitle = "Title repeated on each manual page break.";
	const descLongPageTitle = null;
	const descShorSectionTitle = "Title for each section start";
	const descLongSectionTitle = "The chosen layout defines what excatly a defines a section, and it can be different beween layouts.";

	/* ---------- render ---------- */

	return (
		<div className="flex flex-col flex-grow pt-2">

			<FormGroup>
				<div className="flex gap-4 items-center p-2">
					<div>
						{configuration.paperSize}{' '}
						{getPageLayout(configuration.layout).displayName}
					</div>
					<Button variant="secondary" label="Select Page Type and Calendar Layout ..." onClick={() => setPageTypeSelectorModal(true)} />
				</div>
			</FormGroup>

			{optionVisible('orientation') && (
				<FormGroup>
					{labelFor('orientation', 'Page Orientation')}
					<select
						id="orientation"
						value={orientationValue()}
						disabled={!canChangeOrientations()}
						onChange={e =>
							update(updateProperty('orientation', e.target.value))
						}
					>
						<option value="portrait">Portrait</option>
						<option value="landscape">Landscape</option>
					</select>
				</FormGroup>
			)}

			{optionVisible('pageMargins') && (<FormGroup>
				{labelFor('pageMargins', 'Page Margins')}
				<div className="flex flex-row">
					<div className="flex flex-col p-2">
						<div>Left</div>
						<div className="flex flex-row items-baseline gap-1"><IntegerInput value={configuration.document?.marginsInMm?.left ?? 20} onChange={ (v, isValid) => updateDocumentMarginsInMmObject('left', v)} style={{width: '65px'}} />mm</div>
					</div>
					<div className="flex flex-col p-2">
						<div>Top</div>
						<div className="flex flex-row items-baseline gap-1"><IntegerInput value={configuration.document?.marginsInMm?.top ?? 20} onChange={ (v, isValid) => updateDocumentMarginsInMmObject('top', v)} style={{width: '65px'}} />mm</div>
					</div>
					<div className="flex flex-col p-2">
						<div>Right</div>
						<div className="flex flex-row items-baseline gap-1"><IntegerInput value={configuration.document?.marginsInMm?.right ?? 20} onChange={ (v, isValid) => updateDocumentMarginsInMmObject('right', v)} style={{width: '65px'}} />mm</div>
					</div>
					<div className="flex flex-col p-2">
						<div>Bottom</div>
						<div className="flex flex-row items-baseline gap-1"><IntegerInput value={configuration.document?.marginsInMm?.bottom ?? 20} onChange={ (v, isValid) => updateDocumentMarginsInMmObject('bottom', v)} style={{width: '65px'}} />mm</div>
					</div>
				</div>
			</FormGroup>
			)}

			{optionVisible('years') && (
				<FormGroup>
					{labelFor('years', 'Years')}
					<YearsEditor
						years={years}
						setYears={setYears}
						onResolvedChange={resolved => {
							update(updateProperty('years', resolved))
						}}
					/>
				</FormGroup>
			)}

			{optionVisible('weeks') && (
				<WeekSelector
					defaultWeeks={configuration.weeks}
					refreshPreview={weeks =>
						update(updateProperty('weeks', weeks))
					}
				/>
			)}

			{optionVisible('themeSelector') && (
				<FormGroup>
					{labelFor('themeSelector', 'Theme')}
					<div className="flex p-2">
						<div className="w-40">
							<img
								className="object-contain"
								src={`calendarThemesThumbnails/${typeof themeRef === 'string' ? themeRef : 'custom'
									}.png`}
								alt={activeThemeName}
							/>

							<div className="mt-1">
								{activeThemeName}
							</div>
						</div>

						<div className='flex flex-col justify-center ml-4'>
							<div className="p-2">
								<Button variant="secondary" label="Select Theme ..." onClick={() => setShowThemeSelectorModal(true)} />
							</div>
							<div className="p-2">
								<Button variant="secondary" label="Customize Theme ..." onClick={() => setShowCustomizeThemeModal(true)} />
							</div>
						</div>
					</div>
				</FormGroup>
			)}

			{optionVisible('pageTitle') && (
				<PageTitle
					pageTitleTemplate={configuration.pageTitleTemplate}
					pageTitleTemplateConfig={
						getPageLayout(configuration.layout).pageTitleTemplateConfig
					}
					refreshPreview={(key, value) =>
						update(updateProperty('pageTitleTemplate', value))
					}
				/>
			)}

			{optionVisible('leadingZero') && <FormGroup>
				{labelFor('leadingZero', 'Add leading zero to Numbers')}
				<input id="leadingZero" type="checkbox" defaultChecked={configuration['prependZero']} onChange={event => update(updateProperty('prependZero', event.target.checked))} />
			</FormGroup>}
			{optionVisible('showWeekends') && <FormGroup>
				{labelFor('showWeekends', 'Show Weekends')}
				<input id="showWeekends" type="checkbox" defaultChecked={configuration['showWeekends']} onChange={event => update(updateProperty('showWeekends', event.target.checked))} />
			</FormGroup>}
			{optionVisible('startOnSundays') && <FormGroup>
				{labelFor('startOnSundays', 'Start Weeks on Sundays')}
				<input id="startOnSundays" type="checkbox" defaultChecked={configuration['startWeekOnSundays']} onChange={event => update(updateProperty('startWeekOnSundays', event.target.checked))} />
			</FormGroup>}
			{optionVisible('showWeekColumn') && <FormGroup>
				{labelFor('showWeekColumn', 'Show ISO week column')}
				<input id="showWeekColumn" type="checkbox" defaultChecked={configuration['showWeekColumn']} onChange={event => update(updateProperty('showWeekColumn', event.target.checked))} />
			</FormGroup>}
			{optionVisible('alignmentContentCellHorizontal') && <FormGroup>
				<div className="mb-2 uppercase font-bold text-lg text-grey-darkest">Content Alignment</div>
				<div className='flex gap-x-4'>
					<span className='w-20'>Header</span>
					<label><input className='mr-1' id="alignmentHeaderCell0" type="radio" name='alignmentHeaderCell' defaultChecked={configuration['alignmentHeaderCell'] === 'left'} onChange={event => update(updateProperty('alignmentHeaderCell', 'left'))} />Left</label>
					<label><input className='mr-1' id="alignmentHeaderCell1" type="radio" name='alignmentHeaderCell' defaultChecked={configuration['alignmentHeaderCell'] === 'center'} onChange={event => update(updateProperty('alignmentHeaderCell', 'center'))} />Center</label>
					<label><input className='mr-1' id="alignmentHeaderCell2" type="radio" name='alignmentHeaderCell' defaultChecked={configuration['alignmentHeaderCell'] === 'right'} onChange={event => update(updateProperty('alignmentHeaderCell', 'right'))} />Right</label>
				</div>
				<div className='flex gap-x-4'>
					<span className='w-20'>Content</span>
					<label><input className='mr-1' id="alignmentContentCellHorizontal0" type="radio" name='alignmentContentCellHorizontal' defaultChecked={configuration['alignmentContentCellHorizontal'] === 'left'} onChange={event => update(updateProperty('alignmentContentCellHorizontal', 'left'))} />Left</label>
					<label><input className='mr-1' id="alignmentContentCellHorizontal1" type="radio" name='alignmentContentCellHorizontal' defaultChecked={configuration['alignmentContentCellHorizontal'] === 'center'} onChange={event => update(updateProperty('alignmentContentCellHorizontal', 'center'))} />Center</label>
					<label><input className='mr-1' id="alignmentContentCellHorizontal2" type="radio" name='alignmentContentCellHorizontal' defaultChecked={configuration['alignmentContentCellHorizontal'] === 'right'} onChange={event => update(updateProperty('alignmentContentCellHorizontal', 'right'))} />Right</label>
					<span>/</span>
					<label><input className='mr-1' id="alignmentContentCellVertical0" type="radio" name='alignmentContentCellVertical' defaultChecked={configuration['alignmentContentCellVertical'] === 'top'} onChange={event => update(updateProperty('alignmentContentCellVertical', 'top'))} />Top</label>
					<label><input className='mr-1' id="alignmentContentCellVertical1" type="radio" name='alignmentContentCellVertical' defaultChecked={configuration['alignmentContentCellVertical'] === 'middle'} onChange={event => update(updateProperty('alignmentContentCellVertical', 'middle'))} />Middle</label>
					<label><input className='mr-1' id="alignmentContentCellVertical2" type="radio" name='alignmentContentCellVertical' defaultChecked={configuration['alignmentContentCellVertical'] === 'bottom'} onChange={event => update(updateProperty('alignmentContentCellVertical', 'bottom'))} />Bottom</label>
				</div>
				<div className='flex gap-x-4'>
					<span className='w-20'>ISO Week</span>
					<label><input className='mr-1' id="alignmentWeekCell0" type="radio" name='alignmentWeekCell' defaultChecked={configuration['alignmentWeekCell'] === 'top'} onChange={event => update(updateProperty('alignmentWeekCell', 'top'))} />Top</label>
					<label><input className='mr-1' id="alignmentWeekCell1" type="radio" name='alignmentWeekCell' defaultChecked={configuration['alignmentWeekCell'] === 'middle'} onChange={event => update(updateProperty('alignmentWeekCell', 'middle'))} />Middle</label>
					<label><input className='mr-1' id="alignmentWeekCell2" type="radio" name='alignmentWeekCell' defaultChecked={configuration['alignmentWeekCell'] === 'bottom'} onChange={event => update(updateProperty('alignmentWeekCell', 'bottom'))} />Bottom</label>
				</div>
			</FormGroup>}


			{optionVisible('startOfDay') && (
				<TimeDuration
					startHour={configuration.startOfDay}
					endHour={configuration.endOfDay}
					refreshPreview={(start, end) =>
						update(updateProperty('startOfDay', start, 'endOfDay', end))
					}
				/>
			)}

			{optionVisible('markedDates') && <FormGroup>
				{labelFor('markedDates', 'Mark Dates')}
				<div className="flex flex-row p-2">
					<Button variant="secondary" label="Select Dates ..." onClick={() => setShowSelectDatesModal(true)} />
				</div>
			</FormGroup>}


			{optionVisible('documentHeader') && <TitleOptions title={"Document Header"} value={configuration.document?.header ?? {}} onChange={value => updateDocumentObject('header', value)} variables={headerVariables} descriptionShort={descShortHeader} descriptionLong={descLongHeader} />}
			{optionVisible('documentTitle') && <TitleOptions title={"Document Title"} value={configuration.document?.title ?? {}} onChange={value => updateDocumentObject('title', value)} variables={headerVariables} descriptionShort={descShortDocumentTitle} descriptionLong={descLongDocumentTitle} />}
			{optionVisible('documentPageTitle') && <TitleOptions title={"Page Title"} value={configuration.document?.pageTitle ?? {}} onChange={value => updateDocumentObject('pageTitle', value)} variables={textVariables} descriptionShort={descShorPageTitle} descriptionLong={descLongPageTitle} noTitleCustomization />}
			{optionVisible('documentSectionTitle') && <TitleOptions title={"Section Title"} value={configuration.document?.sectionTitle ?? {}} onChange={value => updateDocumentObject('sectionTitle', value)} variables={textVariables} descriptionShort={descShorSectionTitle} descriptionLong={descLongSectionTitle} />}

			{optionVisible('paddings') && <FormGroup>
				{labelFor('paddings', 'Layout Paddings')}
				<div className="flex flex-row p-2">
					<SliderWithValue label="First Row In Section Top Margin (mm)" min={0} max={100} step={1} initialValue={layoutConfig()?.firstRowMarginTopInMm} cooldownMs={500}
						onChangeCommitted={(value) => {
							update(updateLayoutConfig('firstRowMarginTopInMm', value))
						}} />
				</div>
				<div className="flex flex-row p-2">
					<SliderWithValue label="Row Bottom Margin (mm)" min={0} max={100} step={1} initialValue={layoutConfig()?.marginBottomInMm} cooldownMs={500}
						onChangeCommitted={(value) => {
							update(updateLayoutConfig('marginBottomInMm', value))
						}} />
				</div>
				<div className="flex flex-row p-2">
					<SliderWithValue label="Last Row In Section Bottom Margin (mm)" min={0} max={100} step={1} initialValue={layoutConfig()?.lastRowInSectionMarginBottomInMm} cooldownMs={500}
						onChangeCommitted={(value) => {
							update(updateLayoutConfig('lastRowInSectionMarginBottomInMm', value))
						}} />
				</div>
			</FormGroup>}


			{showPageTypeSelectorModal && (
				<PageTypeSelector
					themes={themes}
					pageLayouts={pageLayouts}
					configuration={configuration}
					refreshPreview={updateCalendarSelection}
					closeModal={() => setPageTypeSelectorModal(false)}
				/>
			)}

			{showThemeSelectorModal && (
				<ThemeSelector
					themes={themes}
					configuration={configuration}
					onThemeSelected={selectTheme}
					closeModal={() => setShowThemeSelectorModal(false)}
				/>
			)}

			{showCustomizeThemeModal && (
				<CustomizeTheme
					data={activeTheme}
					refreshPreview={applyCustomTheme}
					closeModal={() => setShowCustomizeThemeModal(false)}
				/>
			)}

			{showSelectDatesModal && (
				<SelectDates
					colors={colors}
					colorOptions={colorOptions}
					data={optionSelectedDates()}
					refreshPreview={updateSelectedDates}
					importHolidays={() => {
						setShowSelectDatesModal(false);
						setShowImportHolidaysDatesModal(true);
					}}
					importDates={() => {
						setShowSelectDatesModal(false);
						setShowImportDatesModal(true);
					}}
					exportDates={() => {
						setShowSelectDatesModal(false);
						setShowExportDatesModal(true);
					}}
					closeModal={() => setShowSelectDatesModal(false)}
				/>
			)}

			{showImportHolidaysDatesModal && (
				<ImportHolidays
					colors={colors}
					colorOptions={colorOptions}
					data={optionSelectedDates()}
					refreshPreview={updateSelectedDates}
					closeModal={() => {
						setShowImportHolidaysDatesModal(false);
						setShowSelectDatesModal(true);
					}}
				/>
			)}

			{showImportDatesModal && (
				<ImportDates
					refreshPreview={updateSelectedDates}
					closeModal={() => {
						setShowImportDatesModal(false);
						setShowSelectDatesModal(true);
					}}
				/>
			)}

			{showExportDatesModal && (
				<ExportDates
					data={optionSelectedDates()}
					closeModal={() => {
						setShowExportDatesModal(false);
						setShowSelectDatesModal(true);
					}}
				/>
			)}

		</div>
	);
};

export default Customize;